import Empty from "../../../components/Visit/EmptyVisit";

import { Button, DatePicker, Input, Select } from "antd";
import { TableProps } from "antd/es/table";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetBusinessDataQuery } from "../../../features/employee/employeeApi";
import { useGetMyVisitListQuery } from "../../../features/visit/visitApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { MyVisitQueryParams } from "../../../types/visit";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { selectSearchOption, selectStringOption } from "../../../utils/utils";
import useModal from "../../../hooks/useModal";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import { useGetVisitReportMutation } from "../../../features/report/reportApi";

const { RangePicker } = DatePicker;

export enum VisitStatus {
  created = "status-warning",
  completed = "status-success",
  reached = "status-info",
  started = "status-info",
  cancelled = "status-danger",
  rescheduled = "status-dark",
}

interface DataType {
  visitDate: string;
  visitTitle: string;
  note: string;
  status: "created" | "completed" | "reached" | "cancelled";
  _id: string;
}

export const visitTabList = [
  {
    label: "My Visit",
    link: "/admin/visit",
  },
  {
    label: "Team Visit",
    link: "/admin/visit/team-visit",
  },
];

export default function MyVisit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { open, handleClose, handleOpen } = useModal<
    undefined,
    "REPORT_REQUEST"
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handlePagination,
    handleLimit,
    handleFilter,
    handleSearch,
  } = useTable<MyVisitQueryParams>();
  const {
    data: myVisitListData,
    isLoading: isMyVisitListLoading,
    isError,
    error,
  } = useGetMyVisitListQuery({
    params: {
      ...filter,
      search_keyword: keyword,
    },
  });

  const {
    data: businessData,
    isError: isBusinessError,
    error: businessError,
    isLoading: isBusinessLoading,
  } = useGetBusinessDataQuery(authInfo?.business_id?.toString() || "", {
    skip: !authInfo?.business_id,
  });

  // const {
  //   data: myVisitExportData,
  //   isError: isMyVisitExportError,
  //   error: myVisitExportError,
  //   isLoading: isMyVisitExportLoading,
  // } = useGetMyVisitListExportQuery({
  //   params: {
  //     business_member_id: user?.data?.userData?.business_member_id
  //       ? Number(user.data.userData.business_member_id)
  //       : undefined,
  //   },
  // });
  const [
    getVisitReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useGetVisitReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get my visit data fail");
  useError(isBusinessError, businessError, "Get business data fail");

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap w-[165px]">Visit Date</div>,
      dataIndex: "schedule_date",
      render: (val) => (val ? formatDate(val) : "n/a"),
      // render: (val) => (val ? formatDateTime(val) : "n/a"),
    },
    {
      title: "Visit Title",
      dataIndex: "title",
      render: (val) => val || "n/a",
    },
    {
      width: 400,
      title: "Description",
      dataIndex: "description",
      render: (val) => val || "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val: DataType["status"]) => (
        <span className={`${VisitStatus[val]}`}>{capitalize(val)}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <>
          <Link to={`${location.pathname}/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </>
      ),
      align: "center",
    },
  ];

  return (
    <SidebarLayout title="Visit" tabList={visitTabList}>
      {isBusinessLoading ? (
        <Loader />
      ) : businessData?.data?.business?.is_enable_employee_visit ? (
        <CustomTable
          loading={isMyVisitListLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={myVisitListData?.data.total_visits}
          dataSource={myVisitListData?.data.visits}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search by title"
                onChange={handleSearch}
              />
              <RangePicker
                format={dateFormat}
                style={{ width: 260 }}
                suffixIcon={<DateIcon />}
                onChange={(e) => {
                  handleFilter("start_date", e?.[0]?.format(dbDateFormat));
                  handleFilter("end_date", e?.[1]?.format(dbDateFormat));
                }}
              />
              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  Object.keys(VisitStatus),
                  "string",
                  true
                )}
                className="min-w-[120px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                onChange={(e) => handleFilter("status", e)}
              />

              {/* <Button
                className="download-button"
                icon={<DownloadIcon />}
                loading={excelLoading}
                onClick={handleDownload}
              >
                Export
              </Button> */}
              <Button
                onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
                className="download-button"
                icon={<DownloadIcon />}
              >
                Report Requests
              </Button>
            </>
          }
        />
      ) : (
        <Empty
          setShowTable={() => navigate("/admin/settings/visit-settings")}
        />
      )}

      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            getVisitReport({
              body: {
                name: "My visit report",
              },
              params: {
                ...filter,
                skip: 0,
                limit: 0,
                business_member_id: authInfo?.id,
                ...params,
              },
            });
          }}
        />
      )}
    </SidebarLayout>
  );
}

// const data: DataType[] = [
//   {
//     visitDate: "12 Dec, 2024",
//     visitTitle: "Visit title will go here",
//     note: "Note goes here",
//     status: "Created",
//     _id: "1",
//   },
//   {
//     visitDate: "12 Dec, 2024",
//     visitTitle: "Visit title will go here",
//     note: "Note goes here",
//     status: "Completed",
//     _id: "2",
//   },
//   {
//     visitDate: "12 Dec, 2024",
//     visitTitle: "Visit title will go here",
//     note: "Note goes here",
//     status: "Reached",
//     _id: "3",
//   },
// ];
